import { useState } from 'react';
import styled from 'styled-components';
import { useParams } from 'react-router-dom';
import {
  useListPtoChatSessionsQuery,
  useStartPtoChatSessionMutation,
  useGetPtoChatSessionQuery,
  useSendPtoChatMessageMutation,
  useDeletePtoChatSessionMutation,
  type PtoChatSession,
  type PtoChatMessage,
} from '@shared/api';
import { Button } from '@shared/ui/Button';
import { Input } from '@shared/ui/Input';
import { useAgentPermission } from '@entities/user';

const Columns = styled.div`
  display: grid;
  grid-template-columns: 260px 1fr;
  gap: ${({ theme }) => theme.spacing(5)};
  min-height: 60vh;

  @media (max-width: 880px) {
    grid-template-columns: 1fr;
  }
`;

const SessionBtn = styled.button<{ $active: boolean }>`
  display: block;
  width: 100%;
  text-align: left;
  border: 1px solid
    ${({ $active, theme }) => ($active ? theme.colors.primary : theme.colors.border)};
  background: ${({ $active, theme }) =>
    $active ? theme.colors.primarySoft : theme.colors.surface};
  border-radius: ${({ theme }) => theme.radius.md};
  padding: ${({ theme }) => theme.spacing(2.5)};
  margin-bottom: ${({ theme }) => theme.spacing(2)};
  cursor: pointer;
  font: inherit;
  color: inherit;
`;

const Thread = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(3)};
  padding: ${({ theme }) => theme.spacing(4)};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.lg};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  min-height: 360px;
`;

const Bubble = styled.div<{ $user: boolean }>`
  align-self: ${({ $user }) => ($user ? 'flex-end' : 'flex-start')};
  max-width: 80%;
  padding: ${({ theme }) => theme.spacing(3)};
  border-radius: ${({ theme }) => theme.radius.lg};
  background: ${({ $user, theme }) => ($user ? theme.colors.primarySoft : theme.colors.surface)};
  border: 1px solid ${({ theme }) => theme.colors.border};
  white-space: pre-wrap;
  line-height: 1.5;
`;

const Refs = styled.div`
  margin-top: ${({ theme }) => theme.spacing(2)};
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(1.5)};
`;

const RefTag = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.primary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.pill};
  padding: 2px ${({ theme }) => theme.spacing(2)};
`;

const Composer = styled.form`
  display: flex;
  gap: ${({ theme }) => theme.spacing(2)};
  margin-top: ${({ theme }) => theme.spacing(3)};
`;

const Muted = styled.div`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 14px;
`;

export function PtoChat() {
  const { projectId = '' } = useParams();
  const { can_write } = useAgentPermission('pto');
  const { data: sessionsData } = useListPtoChatSessionsQuery(projectId, { skip: !projectId });
  const [startSession] = useStartPtoChatSessionMutation();
  const [sendMessage, { isLoading: sending }] = useSendPtoChatMessageMutation();
  const [deleteSession] = useDeletePtoChatSessionMutation();

  const [activeId, setActiveId] = useState<string | null>(null);
  const [input, setInput] = useState('');

  const { data: activeData } = useGetPtoChatSessionQuery(activeId ?? '', { skip: !activeId });

  const sessions: PtoChatSession[] = sessionsData?.items ?? [];
  const messages: PtoChatMessage[] = activeData?.messages ?? [];

  async function onNew() {
    if (!can_write) return;
    const res = await startSession({ projectId }).unwrap();
    setActiveId(res.session.id);
  }

  async function onSend(e: React.FormEvent) {
    e.preventDefault();
    const text = input.trim();
    if (!text || !can_write) return;
    let sid = activeId;
    if (!sid) {
      const res = await startSession({ projectId }).unwrap();
      sid = res.session.id;
      setActiveId(sid);
    }
    setInput('');
    await sendMessage({ sessionId: sid, content: text }).unwrap();
  }

  async function onDelete(id: string) {
    await deleteSession(id).unwrap();
    if (activeId === id) setActiveId(null);
  }

  return (
    <Columns>
      <div>
        <Button
          $variant="primary"
          $block
          onClick={onNew}
          disabled={!can_write}
          title={!can_write ? 'Только просмотр' : undefined}
          style={{ marginBottom: 12 }}
        >
          Новый чат
        </Button>
        {sessions.length === 0 ? (
          <Muted>Сессий нет.</Muted>
        ) : (
          sessions.map((s) => (
            <SessionBtn key={s.id} $active={activeId === s.id} onClick={() => setActiveId(s.id)}>
              <div style={{ fontWeight: 600, fontSize: 13 }}>{s.title}</div>
              {can_write && (
                <div
                  style={{ fontSize: 11, opacity: 0.7, cursor: 'pointer' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    void onDelete(s.id);
                  }}
                >
                  удалить
                </div>
              )}
            </SessionBtn>
          ))
        )}
      </div>

      <div>
        <Thread>
          {messages.length === 0 ? (
            <Muted>
              Задайте вопрос по рабочей документации проекта. Ответ всегда сопровождается ссылками
              на шифр и лист — если в РД ответа нет, система так и скажет.
            </Muted>
          ) : (
            messages.map((m) => (
              <Bubble key={m.id} $user={m.role === 'user'}>
                {m.content}
                {m.role === 'assistant' && m.refs.length > 0 && (
                  <Refs>
                    {m.refs.map((r, i) => (
                      <RefTag key={`${m.id}-${i}`}>
                        {r.document_code}, стр. {r.page_number}
                        {r.sheet_ref ? `, л. ${r.sheet_ref}` : ''}
                      </RefTag>
                    ))}
                  </Refs>
                )}
              </Bubble>
            ))
          )}
        </Thread>
        <Composer onSubmit={onSend}>
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={can_write ? 'Например: какая толщина стен по АР-1?' : 'Только просмотр'}
            disabled={!can_write}
            style={{ flex: 1 }}
          />
          <Button
            $variant="primary"
            type="submit"
            disabled={sending || !input.trim() || !can_write}
            title={!can_write ? 'Только просмотр' : undefined}
          >
            {sending ? 'Отвечаю…' : 'Спросить'}
          </Button>
        </Composer>
      </div>
    </Columns>
  );
}
